import apiClient from "@/app/api/client";
import type { PaginatedApiResponse, PaginatedData } from "@/shared/types/pagination";
import type {
	CreateDepartmentInput,
	Department,
	DepartmentDetail,
	DepartmentListItem,
	DepartmentSummary,
	UpdateDepartmentInput,
} from "../types";

interface DepartmentListResponse {
	success: true;
	data: Department[];
}

interface SingleDepartmentResponse {
	success: true;
	data: Department;
}

interface DepartmentDetailResponse {
	success: true;
	data: DepartmentDetail;
}

interface DepartmentSummaryResponse {
	success: true;
	data: DepartmentSummary;
}

export const departmentsService = {
	async list(): Promise<Department[]> {
		const response = await apiClient.get<DepartmentListResponse>("/departments");
		return response.data.data;
	},

	async create(input: CreateDepartmentInput): Promise<Department> {
		const response = await apiClient.post<SingleDepartmentResponse>("/departments", input);
		return response.data.data;
	},

	async update(id: string, input: UpdateDepartmentInput): Promise<Department> {
		const response = await apiClient.patch<SingleDepartmentResponse>(`/departments/${id}`, input);
		return response.data.data;
	},

	async remove(id: string): Promise<void> {
		await apiClient.delete(`/departments/${id}`);
	},

	/**
	 * `GET /departments/children` — Explorer sahifasi. `parentId` berilmasa
	 * ildiz tugunlar qaytadi. Backend `paginated()` konverti
	 * `{ success, data: { items, meta } }` — qaytariladigan qiymat ichki
	 * `{ items, meta }` (`usePaginatedQuery` bilan bir xil shakl).
	 */
	async listChildren(params: {
		parentId?: string;
		page: number;
		limit: number;
		q?: string;
		isActive?: boolean;
	}): Promise<PaginatedData<DepartmentListItem>> {
		const response = await apiClient.get<PaginatedApiResponse<DepartmentListItem>>(
			"/departments/children",
			{ params }
		);
		return response.data.data;
	},

	/** `GET /departments/{id}` — `ancestors` (ildiz → bevosita ota) bilan. */
	async getDetail(id: string): Promise<DepartmentDetail> {
		const response = await apiClient.get<DepartmentDetailResponse>(`/departments/${id}`);
		return response.data.data;
	},

	/** `GET /departments/{id}/summary` — profil sahifasining agregat kartalari. */
	async getSummary(id: string): Promise<DepartmentSummary> {
		const response = await apiClient.get<DepartmentSummaryResponse>(`/departments/${id}/summary`);
		return response.data.data;
	},

	/**
	 * `POST /departments/{id}/archive` — javob `ancestors`SIZ `Department`
	 * (backend `DepartmentResponseSchema`), `DepartmentDetail` EMAS.
	 */
	async archive(id: string): Promise<Department> {
		const response = await apiClient.post<SingleDepartmentResponse>(`/departments/${id}/archive`);
		return response.data.data;
	},

	/** `POST /departments/{id}/unarchive` — javob `archive` bilan bir xil shaklda. */
	async unarchive(id: string): Promise<Department> {
		const response = await apiClient.post<SingleDepartmentResponse>(`/departments/${id}/unarchive`);
		return response.data.data;
	},
};
